/* GENERATED FILE — DO NOT EDIT. Source: shared/assets/wishlist.js */
import { Component } from '@theme/component';

/**
 * Wishlist store, shared by every toggle and the drawer.
 *
 * Device-local by design: cross-device sync needs a customer metafield write,
 * which needs an app proxy. Documented as an upgrade path in ADMIN-GUIDE.md.
 */
export class WishlistStore {
  static KEY = 'tira:wishlist';
  static EVENT = 'tira:wishlist:change';

  /** @returns {string[]} */
  static read() {
    try {
      const parsed = JSON.parse(localStorage.getItem(WishlistStore.KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  /** @param {string[]} handles */
  static write(handles) {
    try {
      localStorage.setItem(WishlistStore.KEY, JSON.stringify(handles));
    } catch {
      // Storage unavailable — the in-page state below still updates.
    }
    document.dispatchEvent(new CustomEvent(WishlistStore.EVENT, { detail: { handles } }));
  }

  /** @param {string} handle @returns {boolean} True when the item is now saved. */
  static toggle(handle) {
    const list = WishlistStore.read();
    const index = list.indexOf(handle);
    if (index === -1) list.push(handle);
    else list.splice(index, 1);
    WishlistStore.write(list);
    return index === -1;
  }

  /** @param {string} handle */
  static has(handle) {
    return WishlistStore.read().includes(handle);
  }
}

/**
 * Heart toggle rendered on product cards and product pages.
 * @extends {Component}
 */
class TiraWishlistToggle extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(WishlistStore.EVENT, this.#sync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(WishlistStore.EVENT, this.#sync);
  }

  get #handle() {
    return this.dataset.productHandle ?? '';
  }

  get #button() {
    return this.querySelector('button');
  }

  #handleClick = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element) || !event.target.closest('button')) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#handle) WishlistStore.toggle(this.#handle);
  };

  #sync = () => {
    const saved = this.#handle ? WishlistStore.has(this.#handle) : false;
    this.toggleAttribute('data-saved', saved);
    const button = this.#button;
    if (!button) return;
    button.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.dataset.labelSaved : this.dataset.labelSave;
    if (label) button.setAttribute('aria-label', label);
  };
}

/**
 * Renders the saved products, via the Section Rendering API.
 * @extends {Component}
 */
class TiraWishlistList extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(WishlistStore.EVENT, this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistStore.EVENT, this.#render);
  }

  #render = async () => {
    const list = this.querySelector('[data-wishlist-list]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const sectionId = this.dataset.sectionId;
    if (!list || !sectionId) return;

    const handles = WishlistStore.read();
    const count = this.querySelector('[data-wishlist-count]');
    if (count) count.textContent = String(handles.length);

    if (handles.length === 0) {
      list.innerHTML = '';
      empty?.removeAttribute('hidden');
      return;
    }
    empty?.setAttribute('hidden', '');

    const root = window.Shopify?.routes?.root ?? '/';
    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${handle}?section_id=${sectionId}`);
          if (!response.ok) return null;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          return doc.querySelector('.shopify-section')?.innerHTML ?? null;
        } catch {
          return null;
        }
      }),
    );

    list.innerHTML = cards
      .filter((card) => card !== null)
      .map((card) => `<li class="tira-wishlist__item">${card}</li>`)
      .join('');
  };
}

if (!customElements.get('tira-wishlist-toggle')) customElements.define('tira-wishlist-toggle', TiraWishlistToggle);
if (!customElements.get('tira-wishlist-list')) customElements.define('tira-wishlist-list', TiraWishlistList);
